/**
 * CSV ANALYZER
 * 
 * Analyzes CSV columns to detect patterns and suggest transformations.
 * Helps users understand their data before importing.
 * 
 * Detects:
 * - Dates in various formats
 * - Release types (LE, Core, Exclusive)
 * - Event names (Polish Pickup, Hella Handmade, etc.)
 * - Combined fields that could be split
 * - URLs (including image URLs from Airtable, Google Sheets, etc.)
 */

// Known release type keywords
export const RELEASE_TYPE_KEYWORDS = [
  'le', 'limited edition', 'limited',
  'core', 'permanent',
  'exclusive', 'excl',
  'seasonal',
  'discontinued', 'dc', 'disc',
  'prototype', 'proto',
  'ooak', 'one of a kind',
  'custom',
  'pre-order', 'preorder',
  'rerelease', 're-release',
];

// Known event name patterns
export const EVENT_PATTERNS = [
  { pattern: /polish\s*pick\s*up|ppu/i, name: 'Polish Pickup' },
  { pattern: /hella\s*handmade|hhc/i, name: 'Hella Handmade Creations' },
  { pattern: /indie\s*expo/i, name: 'Indie Expo' },
  { pattern: /polish\s*con/i, name: 'Polish Con' },
  { pattern: /enchanted\s*polish/i, name: 'Enchanted Polish' },
  { pattern: /what\'?s\s*in-?die\s*box/i, name: "What's Indie Box" },
  { pattern: /monthly\s*box/i, name: 'Monthly Box' },
  { pattern: /anniversary/i, name: 'Anniversary' },
  { pattern: /birthday/i, name: 'Birthday' },
  { pattern: /black\s*friday/i, name: 'Black Friday' },
  { pattern: /cyber\s*monday/i, name: 'Cyber Monday' },
  { pattern: /christmas|xmas|holiday/i, name: 'Holiday' },
  { pattern: /valentine/i, name: 'Valentine' },
  { pattern: /halloween/i, name: 'Halloween' },
  { pattern: /collab|collaboration/i, name: 'Collaboration' },
];

// Date patterns to detect
const DATE_PATTERNS = [
  /\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}/, // 2020/01/15 or 2020-01-15
  /\d{4}[\/\-]\d{1,2}/, // 2020/01 or 2020-01
  /\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}/, // 01/15/2020
  /\d{1,2}[\/\-]\d{4}/, // 01/2020
  /(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*\d{4}/i, // Jan 2020
  /\d{4}\s*(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // 2020 Jan
  /^\d{4}$/, // Just year
];

// URL pattern - matches http/https URLs
const URL_PATTERN = /https?:\/\/[^\s"'<>]+/i;

// Image URL patterns - common image hosting services and file extensions
const IMAGE_URL_PATTERNS = [
  // Direct image file extensions
  /\.(jpg|jpeg|png|gif|webp|heic|bmp|tiff?)(\?|$)/i,
  // Airtable attachment URLs
  /dl\.airtable\.com/i,
  /airtableusercontent\.com/i,
  // Google Drive/Photos
  /drive\.google\.com.*\/file/i,
  /lh[0-9]*\.googleusercontent\.com/i,
  // Imgur
  /imgur\.com\/(a\/)?[a-zA-Z0-9]+/i,
  /i\.imgur\.com/i,
  // Dropbox
  /dropbox\.com.*\.(jpg|jpeg|png|gif)/i,
  /dl\.dropboxusercontent\.com/i,
  // AWS S3 buckets (common for apps)
  /s3[.-].*amazonaws\.com/i,
  // Cloudinary
  /cloudinary\.com/i,
  /res\.cloudinary\.com/i,
  // Generic image CDNs
  /images\./i,
  /img\./i,
  /cdn\./i,
];

export interface ColumnAnalysis {
  columnName: string;
  sampleValues: string[];
  uniqueCount: number;
  emptyCount: number;
  totalCount: number;
  // Detection results
  hasDate: boolean;
  datePercentage: number;
  hasReleaseType: boolean;
  releaseTypePercentage: number;
  detectedReleaseTypes: string[];
  hasEventName: boolean;
  eventPercentage: number;
  detectedEvents: string[];
  // URL detection
  hasUrls: boolean;
  urlPercentage: number;
  hasImageUrls: boolean;
  imageUrlPercentage: number;
  detectedUrlDomains: string[];
  // Suggestions
  suggestedField: string | null;
  canBeSplit: boolean;
  splitSuggestion: string | null;
}

export interface AnalysisResult {
  columns: ColumnAnalysis[];
  totalRows: number;
  suggestions: TransformSuggestion[];
  // Summary of detected photo URLs
  photoUrlColumn: string | null;
  photoUrlCount: number;
}

export interface TransformSuggestion {
  id: string;
  columnName: string;
  type: 'split_date' | 'split_release_type' | 'split_event' | 'extract_date' | 'normalize' | 'fetch_photos';
  description: string;
  example: {
    before: string;
    after: { [key: string]: string };
  };
  enabled: boolean;
}

/**
 * Check if a value contains a date
 */
function containsDate(value: string): boolean {
  return DATE_PATTERNS.some(pattern => pattern.test(value));
}

/**
 * Extract date from a value
 */
export function extractDate(value: string): string | null {
  for (const pattern of DATE_PATTERNS) {
    const match = value.match(pattern);
    if (match) {
      return match[0];
    }
  }
  return null;
}

/**
 * Check if a value contains a release type keyword
 */
function containsReleaseType(value: string): string | null {
  const lower = value.toLowerCase();
  for (const keyword of RELEASE_TYPE_KEYWORDS) {
    // Match as whole word or at word boundaries
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(lower)) {
      return keyword;
    }
  }
  return null;
}

/**
 * Check if a value contains an event name
 */
function containsEventName(value: string): string | null {
  for (const event of EVENT_PATTERNS) {
    if (event.pattern.test(value)) {
      return event.name;
    }
  }
  return null;
}

/**
 * Check if a value contains a URL
 */
function containsUrl(value: string): boolean {
  return URL_PATTERN.test(value);
}

/**
 * Extract URL from a value
 */
export function extractUrl(value: string): string | null {
  const match = value.match(URL_PATTERN);
  return match ? match[0] : null;
}

/**
 * Check if a URL is likely an image URL
 */
export function isImageUrl(url: string): boolean {
  return IMAGE_URL_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Extract domain from URL for analysis
 */
function extractDomain(url: string): string | null {
  try {
    const match = url.match(/https?:\/\/([^\/]+)/i);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Analyze a single column
 */
function analyzeColumn(columnName: string, values: string[]): ColumnAnalysis {
  const nonEmpty = values.filter(v => v && v.trim());
  const uniqueValues = new Set(nonEmpty);
  
  // Sample up to 5 unique non-empty values
  const sampleValues = Array.from(uniqueValues).slice(0, 5);
  
  // Detect patterns
  let dateCount = 0;
  let releaseTypeCount = 0;
  let eventCount = 0;
  let urlCount = 0;
  let imageUrlCount = 0;
  const detectedReleaseTypes = new Set<string>();
  const detectedEvents = new Set<string>();
  const detectedDomains = new Set<string>();
  
  for (const value of nonEmpty) {
    if (containsDate(value)) {
      dateCount++;
    }
    
    const releaseType = containsReleaseType(value);
    if (releaseType) {
      releaseTypeCount++;
      detectedReleaseTypes.add(releaseType.toUpperCase());
    }
    
    const eventName = containsEventName(value);
    if (eventName) {
      eventCount++;
      detectedEvents.add(eventName);
    }
    
    // URL detection
    if (containsUrl(value)) {
      urlCount++;
      const url = extractUrl(value);
      if (url) {
        const domain = extractDomain(url);
        if (domain) {
          detectedDomains.add(domain);
        }
        if (isImageUrl(url)) {
          imageUrlCount++;
        }
      }
    }
  }
  
  const totalNonEmpty = nonEmpty.length || 1; // Avoid division by zero
  const datePercentage = Math.round((dateCount / totalNonEmpty) * 100);
  const releaseTypePercentage = Math.round((releaseTypeCount / totalNonEmpty) * 100);
  const eventPercentage = Math.round((eventCount / totalNonEmpty) * 100);
  const urlPercentage = Math.round((urlCount / totalNonEmpty) * 100);
  const imageUrlPercentage = Math.round((imageUrlCount / totalNonEmpty) * 100);
  
  // Determine suggested field based on column name and content
  let suggestedField: string | null = null;
  const lowerName = columnName.toLowerCase();
  
  // Check for URL columns first (by content)
  if (urlPercentage > 50 && imageUrlPercentage > 30) {
    suggestedField = 'photoUrl';
  } else if (lowerName.includes('brand') || lowerName.includes('maker')) {
    suggestedField = 'brand';
  } else if (lowerName.includes('name') || lowerName.includes('polish') || lowerName.includes('shade')) {
    suggestedField = 'name';
  } else if (lowerName.includes('release') && lowerName.includes('year')) {
    suggestedField = 'releaseYear';
  } else if (lowerName.includes('release') && lowerName.includes('type')) {
    suggestedField = 'releaseType';
  } else if (lowerName.includes('release') && lowerName.includes('season')) {
    suggestedField = 'releaseSeason';
  } else if (lowerName.includes('release') || lowerName.includes('released')) {
    suggestedField = 'releaseInfo'; // Combined field - will be parsed
  } else if (lowerName.includes('event') && lowerName.includes('theme')) {
    suggestedField = 'eventTheme';
  } else if (lowerName.includes('event') && lowerName.includes('name')) {
    suggestedField = 'eventSource'; // Just the event name
  } else if (lowerName.includes('event') || lowerName.includes('ppu') || lowerName.includes('pickup')) {
    suggestedField = 'eventInfo'; // Combined - will parse and set releaseType to "event"
  } else if (lowerName.includes('collection') && lowerName.includes('name')) {
    suggestedField = 'collection'; // Just the collection name
  } else if (lowerName.includes('collection') || lowerName.includes('line') || lowerName.includes('series')) {
    suggestedField = 'collectionInfo'; // Combined - will parse and set releaseType to "collection"
  } else if (lowerName.includes('year') || lowerName.includes('date')) {
    suggestedField = 'dateField'; // Standalone date - will extract year/season
  } else if (lowerName.includes('season')) {
    suggestedField = 'releaseSeason';
  } else if (lowerName.includes('color') || lowerName.includes('colour') || lowerName.includes('desc')) {
    suggestedField = 'colorDescription';
  } else if (lowerName.includes('note')) {
    suggestedField = 'notes';
  } else if (lowerName.includes('tag')) {
    suggestedField = 'tags';
  } else if (lowerName.includes('finish') || lowerName.includes('effect')) {
    suggestedField = 'finish';
  } else if (lowerName.includes('fav') || lowerName.includes('love')) {
    suggestedField = 'isFavorite';
  } else if (lowerName.includes('wish') || lowerName.includes('iso') || lowerName.includes('want')) {
    suggestedField = 'onWishlist';
  } else if (lowerName.includes('photo') || lowerName.includes('image') || lowerName.includes('swatch') || lowerName.includes('attachment')) {
    suggestedField = 'photoUrl';
  } else if (lowerName.includes('qty') || lowerName.includes('quantity') || lowerName.includes('count')) {
    suggestedField = 'quantity';
  } else if (lowerName.includes('category') || lowerName.includes('type')) {
    suggestedField = 'category';
  }
  
  // Determine if column can be split
  const hasMultipleTypes = (datePercentage > 20 && releaseTypePercentage > 20) ||
                          (datePercentage > 20 && eventPercentage > 20);
  
  let splitSuggestion: string | null = null;
  if (hasMultipleTypes) {
    if (datePercentage > 20 && releaseTypePercentage > 20) {
      splitSuggestion = 'This column contains both release types and dates. Split into separate fields?';
    } else if (datePercentage > 20 && eventPercentage > 20) {
      splitSuggestion = 'This column contains both event names and dates. Split into separate fields?';
    }
  }
  
  return {
    columnName,
    sampleValues,
    uniqueCount: uniqueValues.size,
    emptyCount: values.length - nonEmpty.length,
    totalCount: values.length,
    hasDate: datePercentage > 10,
    datePercentage,
    hasReleaseType: releaseTypePercentage > 5,
    releaseTypePercentage,
    detectedReleaseTypes: Array.from(detectedReleaseTypes),
    hasEventName: eventPercentage > 5,
    eventPercentage,
    detectedEvents: Array.from(detectedEvents),
    // URL detection results
    hasUrls: urlPercentage > 10,
    urlPercentage,
    hasImageUrls: imageUrlPercentage > 10,
    imageUrlPercentage,
    detectedUrlDomains: Array.from(detectedDomains),
    suggestedField,
    canBeSplit: hasMultipleTypes,
    splitSuggestion,
  };
}

/**
 * Generate transform suggestions based on analysis
 */
function generateSuggestions(columns: ColumnAnalysis[]): TransformSuggestion[] {
  const suggestions: TransformSuggestion[] = [];
  
  for (const col of columns) {
    // Suggest splitting combined release info
    if (col.hasDate && col.hasReleaseType && col.datePercentage > 20 && col.releaseTypePercentage > 20) {
      const sampleValue = col.sampleValues.find(v => containsDate(v) && containsReleaseType(v)) || col.sampleValues[0];
      const extractedDate = extractDate(sampleValue || '');
      const extractedType = containsReleaseType(sampleValue || '');
      
      suggestions.push({
        id: `split_release_${col.columnName}`,
        columnName: col.columnName,
        type: 'split_release_type',
        description: `Split "${col.columnName}" into Release Type and Release Date`,
        example: {
          before: sampleValue || '',
          after: {
            'Release Type': extractedType?.toUpperCase() || 'LE',
            'Release Date': extractedDate || '2022-01'
          }
        },
        enabled: true,
      });
    }
    
    // Suggest splitting combined event info
    if (col.hasDate && col.hasEventName && col.datePercentage > 20 && col.eventPercentage > 20) {
      const sampleValue = col.sampleValues.find(v => containsDate(v) && containsEventName(v)) || col.sampleValues[0];
      const extractedDate = extractDate(sampleValue || '');
      const extractedEvent = containsEventName(sampleValue || '');
      
      suggestions.push({
        id: `split_event_${col.columnName}`,
        columnName: col.columnName,
        type: 'split_event',
        description: `Split "${col.columnName}" into Event Name and Event Date`,
        example: {
          before: sampleValue || '',
          after: {
            'Event': extractedEvent || 'Polish Pickup',
            'Event Date': extractedDate || '2022-07'
          }
        },
        enabled: true,
      });
    }
    
    // Suggest extracting date from collection
    if (col.hasDate && col.datePercentage > 30 && 
        (col.suggestedField === 'collection' || col.columnName.toLowerCase().includes('collection'))) {
      const sampleValue = col.sampleValues.find(v => containsDate(v)) || col.sampleValues[0];
      const extractedDate = extractDate(sampleValue || '');
      // Remove date from the text
      const textWithoutDate = sampleValue?.replace(extractedDate || '', '').replace(/^[\s:\-–—]+|[\s:\-–—]+$/g, '').trim();
      
      suggestions.push({
        id: `extract_date_${col.columnName}`,
        columnName: col.columnName,
        type: 'extract_date',
        description: `Extract date from "${col.columnName}" into a separate Collection Date field`,
        example: {
          before: sampleValue || '',
          after: {
            'Collection': textWithoutDate || 'Collection Name',
            'Collection Date': extractedDate || '2020-01'
          }
        },
        enabled: true,
      });
    }
    
    // Suggest fetching photos from URLs
    if (col.hasImageUrls && col.imageUrlPercentage > 30) {
      const sampleUrl = col.sampleValues.find(v => containsUrl(v) && isImageUrl(extractUrl(v) || '')) || col.sampleValues[0];
      const domain = col.detectedUrlDomains[0] || 'unknown';
      
      suggestions.push({
        id: `fetch_photos_${col.columnName}`,
        columnName: col.columnName,
        type: 'fetch_photos',
        description: `Download and import ${col.urlPercentage}% of rows have image URLs (from ${domain})`,
        example: {
          before: sampleUrl || 'https://example.com/photo.jpg',
          after: {
            'Photo': '✓ Downloaded and uploaded to your collection'
          }
        },
        enabled: true,
      });
    }
  }
  
  return suggestions;
}

/**
 * Analyze an entire CSV file
 */
export function analyzeCSV(headers: string[], rows: string[][]): AnalysisResult {
  const columns: ColumnAnalysis[] = [];
  
  for (let i = 0; i < headers.length; i++) {
    const columnValues = rows.map(row => row[i] || '');
    columns.push(analyzeColumn(headers[i], columnValues));
  }
  
  const suggestions = generateSuggestions(columns);
  
  // Find the best photo URL column
  const photoUrlColumns = columns.filter(c => c.hasImageUrls && c.imageUrlPercentage > 30);
  const bestPhotoColumn = photoUrlColumns.sort((a, b) => b.imageUrlPercentage - a.imageUrlPercentage)[0];
  
  return {
    columns,
    totalRows: rows.length,
    suggestions,
    photoUrlColumn: bestPhotoColumn?.columnName || null,
    photoUrlCount: bestPhotoColumn 
      ? Math.round((bestPhotoColumn.imageUrlPercentage / 100) * rows.length)
      : 0,
  };
}

/**
 * Apply transformations to extract/split data
 */
export function applyTransformations(
  value: string,
  transforms: TransformSuggestion[]
): { [key: string]: string } {
  const result: { [key: string]: string } = { original: value };
  
  for (const transform of transforms) {
    if (!transform.enabled) continue;
    
    switch (transform.type) {
      case 'split_release_type':
        const releaseType = containsReleaseType(value);
        const releaseDate = extractDate(value);
        if (releaseType) result.releaseType = releaseType.toUpperCase();
        if (releaseDate) result.releaseDate = releaseDate;
        // Clean remaining text
        let remaining = value;
        if (releaseDate) remaining = remaining.replace(releaseDate, '');
        if (releaseType) remaining = remaining.replace(new RegExp(`\\b${releaseType}\\b`, 'i'), '');
        remaining = remaining.replace(/^[\s:\-–—,]+|[\s:\-–—,]+$/g, '').trim();
        if (remaining && remaining !== value) result.releaseText = remaining;
        break;
        
      case 'split_event':
        const eventName = containsEventName(value);
        const eventDate = extractDate(value);
        if (eventName) result.event = eventName;
        if (eventDate) result.eventDate = eventDate;
        // Clean remaining text
        let eventRemaining = value;
        if (eventDate) eventRemaining = eventRemaining.replace(eventDate, '');
        eventRemaining = eventRemaining.replace(/^[\s:\-–—,]+|[\s:\-–—,]+$/g, '').trim();
        if (eventRemaining && eventRemaining !== eventName) result.eventText = eventRemaining;
        break;
        
      case 'extract_date':
        const date = extractDate(value);
        if (date) {
          result.date = date;
          result.text = value.replace(date, '').replace(/^[\s:\-–—,]+|[\s:\-–—,]+$/g, '').trim();
        }
        break;
    }
  }
  
  return result;
}

/**
 * Normalize a release type to standard format
 */
export function normalizeReleaseType(value: string): string {
  const lower = value.toLowerCase().trim();
  
  if (/\ble\b|limited\s*edition|limited/.test(lower)) return 'Limited Edition';
  if (/\bcore\b|permanent/.test(lower)) return 'Core';
  if (/exclusive|excl/.test(lower)) return 'Exclusive';
  if (/seasonal/.test(lower)) return 'Seasonal';
  if (/discontinued|dc|disc/.test(lower)) return 'Discontinued';
  if (/prototype|proto/.test(lower)) return 'Prototype';
  if (/ooak|one\s*of\s*a\s*kind/.test(lower)) return 'OOAK';
  if (/custom/.test(lower)) return 'Custom';
  if (/pre-?order/.test(lower)) return 'Pre-order';
  if (/re-?release/.test(lower)) return 'Re-release';
  
  return value; // Return original if no match
}
